/**
 * SpartenDonut — the Sparten breakdown donut (Story UI-U7, AC 5b).
 *
 * recharts PieChart/Pie (innerRadius ~60%), cells assigned STRICTLY in chart-1→5 token
 * order over the sorted slices; slice 5+ merged into an "Übrige" bucket. Sized by premium
 * (fallback: by policy count when premium is gated). Legend = dot + label + mono value;
 * center label = total. German Sparten labels via the local map. Colours from CSS vars only.
 *
 * [Source: docs/stories/ui-u7-portfolio.md AC 5b + Dev Notes §5]
 */

import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import type { SparteSliceDto } from '@brokly/shared';
import { Card } from '../../../components/ui/Card';
import { chf, amount, amountCompact } from '../../../lib/format';
import { sparteLabel } from '../util';

const CHART_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
];

const MAX_NAMED = 4;

interface Slice {
  label: string;
  value: number;
  policen: number;
  praemie: number | null;
}

export function SpartenDonut({ sparten }: { sparten: SparteSliceDto[] }) {
  const premiumVisible = sparten.some((s) => s.praemie_chf !== null);

  // Map to slices in the server order (praemie DESC, or count DESC when gated).
  const mapped: Slice[] = sparten.map((s) => ({
    label: sparteLabel(s.sparte),
    value: premiumVisible ? Number(s.praemie_chf ?? 0) : s.policen,
    policen: s.policen,
    praemie: s.praemie_chf === null ? null : Number(s.praemie_chf),
  }));

  // Keep the top 4 named; merge the rest into a single "Übrige" bucket (design.md §5b).
  let slices: Slice[] = mapped;
  if (mapped.length > MAX_NAMED + 1) {
    const named = mapped.slice(0, MAX_NAMED);
    const rest = mapped.slice(MAX_NAMED);
    const uebrige: Slice = {
      label: 'Übrige',
      value: rest.reduce((acc, r) => acc + r.value, 0),
      policen: rest.reduce((acc, r) => acc + r.policen, 0),
      praemie: premiumVisible ? rest.reduce((acc, r) => acc + (r.praemie ?? 0), 0) : null,
    };
    slices = [...named, uebrige];
  }

  const total = slices.reduce((acc, s) => acc + s.value, 0);
  // Compact + no "CHF" to fit the hole; exact amount kept in the accessible label/title.
  const totalLabel = premiumVisible ? amountCompact(total) : `${total} Policen`;
  const totalExact = premiumVisible ? chf(total) : `${total} Policen`;

  return (
    <Card className="rounded-xl p-4">
      <div className="mb-1 text-sm font-medium">Sparten</div>
      <div className="mb-3 text-xs text-muted-foreground">
        {premiumVisible ? 'Prämienvolumen nach Sparte, in CHF' : 'Policenanzahl nach Sparte'}
      </div>
      {slices.length === 0 || total === 0 ? (
        <div className="grid h-40 place-items-center text-sm text-muted-foreground">
          Keine Sparten-Daten.
        </div>
      ) : (
        <div className="flex flex-col items-center gap-4 sm:flex-row">
          <div className="relative h-40 w-40 shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={slices}
                  dataKey="value"
                  nameKey="label"
                  innerRadius="68%"
                  outerRadius="100%"
                  paddingAngle={2}
                  stroke="var(--card)"
                  strokeWidth={2}
                  isAnimationActive={false}
                >
                  {slices.map((_s, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 grid place-items-center">
              {/* role=img + aria-label voice the exact amount; the compacted number is aria-hidden. */}
              <div
                role="img"
                aria-label={`${totalExact} Gesamt`}
                title={totalExact}
                className="max-w-[108px] text-center leading-tight"
              >
                {/* max-w + nowrap keep it on one line inside the hole. */}
                <div
                  className="mono-num whitespace-nowrap text-sm font-semibold text-foreground"
                  aria-hidden="true"
                >
                  {totalLabel}
                </div>
                <div className="text-[11px] text-muted-foreground" aria-hidden="true">
                  Gesamt
                </div>
              </div>
            </div>
          </div>
          <ul className="min-w-0 flex-1 space-y-1.5">
            {slices.map((s, i) => (
              <li key={i} className="flex items-center gap-2 text-sm">
                <span
                  className="size-2 shrink-0 rounded-full"
                  style={{ background: CHART_COLORS[i % CHART_COLORS.length] }}
                />
                <span className="min-w-0 flex-1 truncate">{s.label}</span>
                {premiumVisible ? (
                  <span className="mono-num text-muted-foreground">
                    {/* Visible without "CHF"; sr-only keeps the currency for screen readers. */}
                    <span aria-hidden="true">{amount(s.praemie ?? 0)}</span>
                    <span className="sr-only">{chf(s.praemie ?? 0)}</span>
                  </span>
                ) : (
                  <span className="mono-num text-muted-foreground">{s.policen}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}
